// biome-ignore-all lint/a11y: TODO - address a11y issues later
import {
  ChevronDownIcon,
  Cross2Icon,
  Pencil1Icon,
  StarFilledIcon,
  StarIcon,
} from "@radix-ui/react-icons";
import { useMutation } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  Copy,
  Plus,
  RotateCcw,
  Save,
  Share2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type SavedView,
  useCreateSavedView,
  useDeleteSavedView,
  useSavedViews,
  useUpdateSavedView,
} from "@/hooks/api/use-saved-views";
import { useAuth } from "@/hooks/auth/use-auth";
import { useSavedViewState } from "@/hooks/use-saved-view-state";
import { api } from "@/lib/api";
import { captureBrowserState } from "@/lib/saved-view-utils";
import { cn } from "@/lib/utils";

interface ViewSelectorProps {
  id?: string;
  pageType: string;
  className?: string;
}

export function ViewSelector({ id, pageType, className }: ViewSelectorProps) {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationId = user?.company?.default_organization?.id || "";

  // API hooks
  const { data: savedViews = [], isLoading: isLoadingViews } =
    useSavedViews(pageType);
  const createView = useCreateSavedView();
  const deleteView = useDeleteSavedView();

  // Saved view state management (includes all tracking functions)
  const {
    currentViewId,
    hasChanges,
    loadView,
    clearView,
    clearChanges,
    resetToCurrentView,
    personalDefaultId,
    setPersonalDefault,
  } = useSavedViewState({
    pageType,
  });

  // Find the currently selected view (null = system default)
  const selectedView = currentViewId
    ? savedViews.find((v) => v.id === currentViewId) || null
    : null;
  const isSystemDefault = !currentViewId && !isLoadingViews;

  // Update mutation hook
  const updateView = useUpdateSavedView();

  // Dialog states
  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sharePopoverOpen, setSharePopoverOpen] = useState(false);
  const [shareUrlCopied, setShareUrlCopied] = useState(false);
  const [viewSelectorOpen, setViewSelectorOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Share states
  const [shareMode, setShareMode] = useState<"saved" | "onetime">("saved");
  const [oneTimeShareUrl, setOneTimeShareUrl] = useState<string | null>(null);
  const [oneTimeShareCopied, setOneTimeShareCopied] = useState(false);

  // Form states
  const [newViewName, setNewViewName] = useState("");
  const [newViewDescription, setNewViewDescription] = useState("");
  const [editingView, setEditingView] = useState<SavedView | null>(null);

  // One-time share URL mutation
  const createOneTimeShareUrl = useMutation({
    mutationFn: async (state: Record<string, unknown>) => {
      return api.post<{ url: string; short_id: string }>("/s/", {
        state,
      });
    },
  });

  // Filter views based on search term
  const filteredViews = useMemo(() => {
    if (!searchTerm) return savedViews;
    const term = searchTerm.toLowerCase();
    return savedViews.filter(
      (view) =>
        view.name.toLowerCase().includes(term) ||
        view.description?.toLowerCase().includes(term),
    );
  }, [savedViews, searchTerm]);

  const handleSelectView = (viewId: string | null) => {
    loadView(viewId);
    setViewSelectorOpen(false);
    setSearchTerm("");
  };

  const handleSaveChanges = async () => {
    if (selectedView) {
      // Update existing saved view
      const currentState = captureBrowserState(pageType, organizationId);
      await updateView.mutateAsync({
        viewId: selectedView.id,
        state_data: currentState,
      });
      clearChanges();
    } else {
      // On System Default - should save as new
      handleSaveAsNew();
    }
  };

  const handleSaveAsNew = () => {
    const timestamp = new Date().toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    setNewViewName(`View - ${timestamp}`);
    setNewViewDescription("");
    setSaveAsDialogOpen(true);
  };

  const handleCreateNewView = async () => {
    const currentState = captureBrowserState(pageType, organizationId);
    const newView = await createView.mutateAsync({
      name: newViewName,
      description: newViewDescription,
      page_type: pageType,
      state_data: currentState,
    });
    setSaveAsDialogOpen(false);
    // Navigate to the new view
    loadView(newView.id);
  };

  const handleEditView = (view: SavedView) => {
    setEditingView(view);
    setNewViewName(view.name);
    setNewViewDescription(view.description || "");
    setEditDialogOpen(true);
  };

  const handleUpdateView = async () => {
    if (!editingView) return;

    await updateView.mutateAsync({
      viewId: editingView.id,
      name: newViewName,
      description: newViewDescription,
    });
    setEditDialogOpen(false);
  };

  const handleDeleteView = (view: SavedView) => {
    setEditingView(view);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!editingView) return;

    await deleteView.mutateAsync({
      viewId: editingView.id,
      pageType: pageType,
    });
    setDeleteDialogOpen(false);

    if (selectedView?.id === editingView?.id) {
      clearView();
      // Remove ?view from URL if it points to the deleted view
      const next = new URLSearchParams(searchParams);
      next.delete("view");
      setSearchParams(next, { replace: true });
    }
  };

  const handleSetPersonalDefault = (viewId: string | null) => {
    const newDefault = personalDefaultId === viewId ? null : viewId;
    setPersonalDefault(newDefault);
  };

  const handleReset = () => {
    // Use the same reset function as the table's reset menu
    resetToCurrentView();
    toast.success("Table reset to saved view configuration");
  };

  const handleShareClick = () => {
    setSharePopoverOpen(true);
    setShareUrlCopied(false);
    setOneTimeShareCopied(false);
    setOneTimeShareUrl(null);
    // Default to saved view mode if there's a selected view, otherwise one-time
    setShareMode(selectedView ? "saved" : "onetime");
  };

  const handleCopyShareUrl = async () => {
    if (!selectedView) return;
    const url = `${window.location.origin}${window.location.pathname}?view=${selectedView.id}`;
    await navigator.clipboard.writeText(url);
    setShareUrlCopied(true);
    setTimeout(() => {
      setShareUrlCopied(false);
    }, 2000);
  };

  const getShareUrl = () => {
    if (!selectedView) return "";
    return `${window.location.origin}${window.location.pathname}?view=${selectedView.id}`;
  };

  const handleCreateOneTimeShare = async () => {
    if (!organizationId) return;

    try {
      // Capture full browser state then filter to this pageType scope
      const fullState = captureBrowserState(pageType, organizationId) as {
        sessionStorage: Record<string, unknown>;
        localStorage: Record<string, unknown>;
      };

      const state = {
        page: location.pathname,
        orgId: organizationId,
        sessionStorage: fullState.sessionStorage || {},
        localStorage: fullState.localStorage || {},
        timestamp: new Date().toISOString(),
      };

      const response = await createOneTimeShareUrl.mutateAsync(state);
      const fullUrl = `${window.location.origin}${response.url}`;
      setOneTimeShareUrl(fullUrl);
    } catch {
      toast.error("Failed to create share URL. Please try again.");
    }
  };

  const handleCopyOneTimeShareUrl = async () => {
    if (!oneTimeShareUrl) return;
    await navigator.clipboard.writeText(oneTimeShareUrl);
    setOneTimeShareCopied(true);
    setTimeout(() => {
      setOneTimeShareCopied(false);
    }, 2000);
  };

  return (
    <div id={id} className={cn("flex flex-wrap items-center gap-2", className)}>
      {/* Save Button with Dropdown - Now on the left */}
      {hasChanges && (
        <div className="flex order-2 md:order-1 basis-full md:basis-auto">
          <Button
            data-testid="view-selector-save-button"
            onClick={handleSaveChanges}
            className="rounded-r-none"
          >
            <Save className="h-4 w-4 mr-2" />
            {selectedView ? "Save for everyone" : "Save as new view"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="view-selector-more-menu"
                variant="default"
                className="rounded-l-none border-l border-l-background/20 px-2"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Only show "Save as new view" option if we're not on System Default */}
              {selectedView && (
                <DropdownMenuItem onClick={handleSaveAsNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Save as new view
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid="reset-to-saved"
                onClick={handleReset}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset to saved
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* View Selector Dropdown */}
      <Popover open={viewSelectorOpen} onOpenChange={setViewSelectorOpen}>
        <PopoverTrigger asChild>
          <Button
            data-testid="view-selector-button"
            variant="outline"
            className="order-1 md:order-2 justify-between gap-6 min-w-[250px] max-w-full overflow-hidden bg-white dark:bg-[hsl(195_30%_12%)]"
            role="combobox"
            aria-expanded={viewSelectorOpen}
          >
            <span className="flex items-center gap-2 flex-1 min-w-0">
              {selectedView ? (
                <>
                  <span className="truncate">{selectedView.name}</span>
                  {hasChanges && (
                    <Badge
                      variant="secondary"
                      className="h-5 px-1 text-xs shrink-0"
                    >
                      Modified
                    </Badge>
                  )}
                </>
              ) : isSystemDefault ? (
                <>
                  <span className="truncate">System Default</span>
                  {hasChanges && (
                    <Badge
                      variant="secondary"
                      className="h-5 px-1 text-xs shrink-0"
                    >
                      Modified
                    </Badge>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">Select a view</span>
              )}
            </span>
            <ChevronDownIcon className="h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[400px] max-w-[90vw] max-h-[70vh] overflow-hidden"
          align="end"
          sideOffset={4}
        >
          <Command className="flex h-full w-full flex-col overflow-hidden rounded-lg corner-smooth-lg bg-popover text-popover-foreground">
            <CommandInput
              placeholder="Search views..."
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList className="overflow-y-auto max-h-[300px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-md">
              <CommandEmpty>
                <div className="py-6 text-center text-sm">No views found</div>
              </CommandEmpty>

              {/* System Default - Top Section */}
              <CommandGroup>
                <CommandItem
                  value="system-default"
                  onSelect={() => handleSelectView(null)}
                  className="flex items-center justify-between py-2.5 cursor-pointer"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isSystemDefault && <Check className="h-3 w-3" />}
                    <span
                      className={cn(
                        "truncate",
                        isSystemDefault && "font-medium",
                      )}
                    >
                      System Default
                    </span>
                  </div>

                  {/* Actions for System Default */}
                  <div className="flex items-center">
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            className="p-1 hover:bg-accent rounded h-7 w-7 flex items-center justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleSetPersonalDefault(null);
                            }}
                          >
                            {personalDefaultId === null ? (
                              <StarFilledIcon className="h-3.5 w-3.5 text-primary" />
                            ) : (
                              <StarIcon className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {personalDefaultId === null
                            ? "Your personal default"
                            : "Set as personal default"}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </CommandItem>
              </CommandGroup>

              {/* Separator between System Default and Saved Views */}
              <div className="my-1 h-px bg-border" />

              {/* Saved Views Section with + New button */}
              <div className="flex justify-between items-center px-2 py-1.5 text-xs font-medium text-muted-foreground">
                <span>Saved Views</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 py-0.5 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setViewSelectorOpen(false);
                    setSearchTerm("");
                    handleSaveAsNew();
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  New
                </Button>
              </div>

              <CommandGroup>
                {/* Saved Views */}
                {filteredViews.length === 0 && searchTerm === "" ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No saved views yet
                  </div>
                ) : (
                  filteredViews.map((view) => (
                    <CommandItem
                      key={view.id}
                      value={`${view.name} ${view.description || ""}`}
                      onSelect={() => handleSelectView(view.id)}
                      className="flex items-center justify-between py-2.5 cursor-pointer"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {selectedView?.id === view.id && (
                          <Check className="h-3 w-3" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span
                            className={cn(
                              "block break-words",
                              selectedView?.id === view.id && "font-medium",
                            )}
                          >
                            {view.name}
                          </span>
                        </div>
                      </div>

                      {/* Actions always visible */}
                      <div className="flex items-center">
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                className="p-1 hover:bg-accent rounded h-7 w-7 flex items-center justify-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleSetPersonalDefault(view.id);
                                }}
                              >
                                {personalDefaultId === view.id ? (
                                  <StarFilledIcon className="h-3.5 w-3.5 text-primary" />
                                ) : (
                                  <StarIcon className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {personalDefaultId === view.id
                                ? "Your personal default"
                                : "Set as personal default"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                className="p-1 hover:bg-accent rounded h-7 w-7 flex items-center justify-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setViewSelectorOpen(false);
                                  setSearchTerm("");
                                  handleEditView(view);
                                }}
                              >
                                <Pencil1Icon className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Edit view</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                className="p-1 hover:bg-accent rounded h-7 w-7 flex items-center justify-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setViewSelectorOpen(false);
                                  setSearchTerm("");
                                  handleDeleteView(view);
                                }}
                              >
                                <Cross2Icon className="h-3.5 w-3.5 text-destructive hover:text-destructive/90" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Delete view</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Share Button */}
      <Popover open={sharePopoverOpen} onOpenChange={setSharePopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            onClick={handleShareClick}
            size="icon"
            className="order-3 bg-white dark:bg-secondary"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[480px] pt-2" align="end">
          <Tabs
            value={shareMode}
            onValueChange={(value) =>
              setShareMode(value as "saved" | "onetime")
            }
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              {!selectedView ? (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="w-full inline-flex">
                        <TabsTrigger
                          value="saved"
                          disabled={true}
                          className="w-full pointer-events-none"
                        >
                          Saved View
                        </TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Select a saved view first to share it
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <TabsTrigger value="saved">Saved View</TabsTrigger>
              )}
              <TabsTrigger value="onetime">One-Time Share</TabsTrigger>
            </TabsList>

            <TabsContent value="saved" className="space-y-4">
              <div>
                <h4 className="font-medium text-sm mb-1">Share saved view</h4>
                <p className="text-xs text-muted-foreground">
                  This link loads the "{selectedView?.name}" view, including all
                  future changes.
                </p>
              </div>
              <div className="flex">
                <input
                  value={getShareUrl()}
                  readOnly
                  className="flex-1 h-9 rounded-l-md border border-input bg-transparent px-3 py-1 text-xs font-mono shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 border-r-0"
                  onClick={(e) => e.currentTarget.select()}
                />
                <Button
                  onClick={handleCopyShareUrl}
                  variant="default"
                  disabled={shareUrlCopied}
                  className={`h-9 rounded-l-none border-l-0 px-3 shrink-0 transition-all duration-300 ease-in-out ${
                    shareUrlCopied ? "w-[100px]" : "w-[80px]"
                  }`}
                >
                  {shareUrlCopied ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="onetime" className="space-y-4">
              <div>
                <h4 className="font-medium text-sm mb-1">One-time share URL</h4>
                <p className="text-xs text-muted-foreground">
                  This link captures the current page state (filters,
                  selections, etc.) at this moment. Future changes won't be
                  reflected.
                </p>
              </div>
              {!oneTimeShareUrl ? (
                <Button
                  onClick={handleCreateOneTimeShare}
                  variant="default"
                  className="w-full"
                  disabled={createOneTimeShareUrl.isPending}
                >
                  {createOneTimeShareUrl.isPending ? (
                    <>Creating...</>
                  ) : (
                    <>Generate Share URL</>
                  )}
                </Button>
              ) : (
                <div className="flex">
                  <input
                    value={oneTimeShareUrl}
                    readOnly
                    className="flex-1 h-9 rounded-l-md border border-input bg-transparent px-3 py-1 text-xs font-mono shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 border-r-0"
                    onClick={(e) => e.currentTarget.select()}
                  />
                  <Button
                    onClick={handleCopyOneTimeShareUrl}
                    variant="default"
                    disabled={oneTimeShareCopied}
                    className={`h-9 rounded-l-none border-l-0 px-3 shrink-0 transition-all duration-300 ease-in-out ${
                      oneTimeShareCopied ? "w-[100px]" : "w-[80px]"
                    }`}
                  >
                    {oneTimeShareCopied ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>

      {/* Save As New View Dialog */}
      <Dialog open={saveAsDialogOpen} onOpenChange={setSaveAsDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Save as New View</DialogTitle>
            <DialogDescription>
              Create a new saved view with the current configuration
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder="e.g. Q4 Pipeline"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={newViewDescription}
                onChange={(e) => setNewViewDescription(e.target.value)}
                placeholder="Describe what this view shows..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveAsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateNewView} disabled={!newViewName}>
              Create View
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit View Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Edit View</DialogTitle>
            <DialogDescription>
              Update the name and description of this saved view
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder="e.g. Q4 Pipeline"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description (optional)</Label>
              <Textarea
                id="edit-description"
                value={newViewDescription}
                onChange={(e) => setNewViewDescription(e.target.value)}
                placeholder="Describe what this view shows..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateView} disabled={!newViewName}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Delete View</DialogTitle>
            <DialogDescription>
              Deleting "{editingView?.name}" will remove this saved view for all
              users in your organization. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Delete View
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
